import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle2, Mail, Clock, ArrowRight, Loader2 } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    country: '',
    project_type: '',
    message: '',
    consent: false,
    website: '', // honeypot
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSelectChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const v = t('contact.validation', { returnObjects: true }) || {};
    const newErrors = {};
    if (!form.company.trim()) newErrors.company = v.company_required;
    if (!form.name.trim()) newErrors.name = v.name_required;
    if (!form.email.trim()) newErrors.email = v.email_required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = v.email_invalid;
    if (!form.country) newErrors.country = v.country_required;
    if (!form.project_type) newErrors.project_type = v.project_type_required;
    if (!form.message.trim()) newErrors.message = v.message_required;
    if (!form.consent) newErrors.consent = v.consent_required;
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, {
        ...form,
        language: localStorage.getItem('energiesse-lang') || 'en',
      });
      setSuccess(true);
      toast.success(t('contact.form_success_title'), {
        description: t('contact.form_success'),
      });
    } catch (err) {
      toast.error(t('contact.form_error'));
    } finally {
      setLoading(false);
    }
  };

  const countries = t('contact.countries', { returnObjects: true }) || [];
  const projectTypes = t('contact.project_types', { returnObjects: true }) || [];

  return (
    <>
      <Helmet>
        <title>Contact — ENERGIESSE SL Partnership Inquiry</title>
        <meta
          name="description"
          content="Request a partnership call with ENERGIESSE SL. B2B inquiry form for solar EPCs and energy operators."
        />
      </Helmet>

      {/* Page Hero */}
      <section className="pt-14 pb-10 bg-[var(--energy-bg)] border-b border-border">
        <div className="container-site">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl space-y-4"
          >
            <motion.p variants={fadeUp} className="text-xs font-medium text-[var(--energy-green)] uppercase tracking-widest">
              {t('nav.contact')}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-3xl md:text-4xl font-semibold tracking-tight"
            >
              {t('contact.headline')}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base text-muted-foreground leading-relaxed max-w-[55ch]">
              {t('contact.subtitle')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding bg-white">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left: Contact info */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="space-y-6"
            >
              <motion.div variants={fadeUp} className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[var(--energy-green-soft)] flex items-center justify-center flex-shrink-0">
                    <Mail className="h-4 w-4 text-[var(--energy-green)]" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('contact.email_label')}</p>
                    <a
                      href="mailto:info@energiesse.com"
                      className="font-medium text-sm text-foreground hover:text-[var(--energy-green)] transition-colors"
                      data-testid="contact-email-link"
                    >
                      {t('contact.email_value')}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[var(--energy-green-soft)] flex items-center justify-center flex-shrink-0">
                    <Clock className="h-4 w-4 text-[var(--energy-green)]" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Response time</p>
                    <p className="font-medium text-sm">{t('contact.response_note')}</p>
                  </div>
                </div>
              </motion.div>

              <Separator />

              <motion.div variants={fadeUp} className="space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">ENERGIESSE SL</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Comunidad Valenciana, Spain<br />
                  Available EU-wide
                </p>
              </motion.div>
            </motion.div>

            {/* Right: Form */}
            <div className="lg:col-span-2">
              {success ? (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-8 rounded-2xl border border-[var(--energy-green)]/30 bg-[var(--energy-green-soft)] text-center space-y-3"
                  data-testid="contact-success-message"
                >
                  <CheckCircle2 className="h-10 w-10 text-[var(--energy-green)] mx-auto" />
                  <h3 className="font-heading font-semibold text-lg">{t('contact.form_success_title')}</h3>
                  <p className="text-sm text-muted-foreground">{t('contact.form_success')}</p>
                </motion.div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-5"
                  data-testid="contact-form"
                >
                  <div>
                    <h2 className="font-heading font-semibold text-lg">{t('contact.form_title')}</h2>
                  </div>

                  {/* Honeypot - hidden */}
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    className="hidden"
                    tabIndex="-1"
                    autoComplete="off"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Company */}
                    <div className="space-y-1.5">
                      <Label htmlFor="company" className="text-sm">{t('contact.form_company')}</Label>
                      <Input
                        id="company"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder={t('contact.form_company_placeholder')}
                        className={errors.company ? 'border-destructive' : ''}
                        data-testid="contact-company-input"
                      />
                      {errors.company && <p className="text-xs text-destructive">{errors.company}</p>}
                    </div>

                    {/* Name */}
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-sm">{t('contact.form_name')}</Label>
                      <Input
                        id="name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder={t('contact.form_name_placeholder')}
                        className={errors.name ? 'border-destructive' : ''}
                        data-testid="contact-name-input"
                      />
                      {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                    </div>

                    {/* Email */}
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-sm">{t('contact.form_email')}</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder={t('contact.form_email_placeholder')}
                        className={errors.email ? 'border-destructive' : ''}
                        data-testid="contact-email-input"
                      />
                      {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                    </div>

                    {/* Phone */}
                    <div className="space-y-1.5">
                      <Label htmlFor="phone" className="text-sm">{t('contact.form_phone')}</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder={t('contact.form_phone_placeholder')}
                        data-testid="contact-phone-input"
                      />
                    </div>

                    {/* Country */}
                    <div className="space-y-1.5">
                      <Label className="text-sm">{t('contact.form_country')}</Label>
                      <Select
                        onValueChange={(val) => handleSelectChange('country', val)}
                        value={form.country}
                      >
                        <SelectTrigger
                          className={errors.country ? 'border-destructive' : ''}
                          data-testid="contact-country-select"
                        >
                          <SelectValue placeholder={t('contact.form_country_placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((c, i) => (
                            <SelectItem key={i} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.country && <p className="text-xs text-destructive">{errors.country}</p>}
                    </div>

                    {/* Project Type */}
                    <div className="space-y-1.5">
                      <Label className="text-sm">{t('contact.form_project_type')}</Label>
                      <Select
                        onValueChange={(val) => handleSelectChange('project_type', val)}
                        value={form.project_type}
                      >
                        <SelectTrigger
                          className={errors.project_type ? 'border-destructive' : ''}
                          data-testid="contact-project-type-select"
                        >
                          <SelectValue placeholder={t('contact.form_project_type_placeholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {projectTypes.map((pt, i) => (
                            <SelectItem key={i} value={pt}>{pt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.project_type && <p className="text-xs text-destructive">{errors.project_type}</p>}
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-1.5">
                    <Label htmlFor="message" className="text-sm">{t('contact.form_message')}</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder={t('contact.form_message_placeholder')}
                      className={`min-h-28 ${errors.message ? 'border-destructive' : ''}`}
                      data-testid="contact-message-textarea"
                    />
                    {errors.message && <p className="text-xs text-destructive">{errors.message}</p>}
                  </div>

                  {/* Consent */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="consent"
                      name="consent"
                      checked={form.consent}
                      onChange={handleChange}
                      className="mt-0.5 h-4 w-4 rounded border-border accent-[var(--energy-green)]"
                      data-testid="contact-consent-checkbox"
                    />
                    <div>
                      <Label htmlFor="consent" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
                        {t('contact.form_consent')}
                      </Label>
                      {errors.consent && <p className="text-xs text-destructive mt-0.5">{errors.consent}</p>}
                    </div>
                  </div>

                  {/* Submit */}
                  <div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full md:w-auto bg-[var(--energy-green)] hover:bg-[var(--energy-green)]/90 text-white btn-primary h-11"
                      data-testid="contact-submit-button"
                    >
                      {loading ? (
                        <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t('common.loading')}</>
                      ) : (
                        <>{t('contact.form_submit')}<ArrowRight className="ml-2 h-4 w-4" /></>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
